/**
 * QuadrantColumn — une colonne (quadrant) de la matrice d'Eisenhower.
 * Zone droppable pour dnd-kit + liste sortable des TaskCard.
 */
import type { JSX, ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Quadrant, Task } from '@store/types';
import { TaskCard } from './TaskCard';

export interface QuadrantMeta {
  id: Quadrant;
  title: string;
  subtitle: string;
  accent: string;
  background: string;
}

export const QUADRANTS: QuadrantMeta[] = [
  {
    id: 'Q1',
    title: 'Q1 · Urgent + Important',
    subtitle: 'À faire immédiatement',
    accent: 'var(--color-urgent)',
    background: 'rgba(239, 83, 80, 0.05)',
  },
  {
    id: 'Q2',
    title: 'Q2 · Important',
    subtitle: 'À planifier',
    accent: 'var(--color-important)',
    background: 'rgba(66, 165, 245, 0.05)',
  },
  {
    id: 'Q3',
    title: 'Q3 · Urgent',
    subtitle: 'À traiter rapidement',
    accent: 'var(--color-normal)',
    background: 'rgba(102, 187, 106, 0.05)',
  },
  {
    id: 'Q4',
    title: 'Q4 · À déléguer',
    subtitle: 'À différer ou confier',
    accent: 'var(--color-defer)',
    background: 'rgba(189, 189, 189, 0.08)',
  },
];

export interface QuadrantColumnProps {
  meta: QuadrantMeta;
  tasks: Task[];
  onAdd?: (quadrant: Quadrant) => void;
  onStartTask?: (task: Task) => void;
  onCompleteTask?: (task: Task) => void;
}

export function QuadrantColumn({
  meta,
  tasks,
  onAdd,
  onStartTask,
  onCompleteTask,
}: QuadrantColumnProps): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: meta.id,
    data: { quadrant: meta.id },
  });

  return (
    <section
      ref={setNodeRef}
      className="rounded-card border p-4 flex flex-col min-h-[320px] transition-unique"
      style={{
        background: isOver ? 'var(--color-primary-light)' : meta.background,
        borderColor: isOver ? 'var(--color-primary-dark)' : 'var(--color-border)',
        borderWidth: isOver ? 2 : 1,
      }}
      aria-label={meta.title}
    >
      <header className="flex items-start justify-between mb-3">
        <div>
          <h3
            className="text-sm font-bold m-0 inline-flex items-center gap-2"
            style={{ color: meta.accent }}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: meta.accent }}
              aria-hidden
            />
            {meta.title}
          </h3>
          <p className="text-[11px] text-[color:var(--color-text-medium)] mt-0.5">
            {meta.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="unique-badge unique-badge--gold">{tasks.length}</span>
          <button
            type="button"
            onClick={() => onAdd?.(meta.id)}
            className="unique-btn-ghost !p-1.5"
            aria-label={`Ajouter une tâche dans ${meta.title}`}
          >
            <Plus size={16} />
          </button>
        </div>
      </header>

      <SortableContext
        items={tasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-3 flex-1">
          {tasks.length === 0 ? (
            <EmptySlot label={meta.title} />
          ) : (
            tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onStart={onStartTask}
                onComplete={onCompleteTask}
              />
            ))
          )}
        </div>
      </SortableContext>
    </section>
  );
}

function EmptySlot({ label }: { label: string }): ReactNode {
  return (
    <div className="flex-1 flex items-center justify-center text-center rounded-card border border-dashed border-[color:var(--color-border)] p-6">
      <p className="text-xs text-[color:var(--color-text-medium)]">
        Glissez une tâche ici ou cliquez sur <span aria-hidden>+</span>
        <br />
        pour ajouter à <strong>{label}</strong>.
      </p>
    </div>
  );
}
